import type { DataContext } from '@calculom/report-designer';

interface ReportParameterHeaderProps {
  reportName: string;
  dataContext: DataContext;
  selectedCompany: string;
  selectedPeriod: string;
}

export function ReportParameterHeader({
  reportName,
  dataContext,
  selectedCompany,
  selectedPeriod,
}: ReportParameterHeaderProps) {
  const { dateFiscalInfo, erpConnections, companies } = dataContext;
  const companiesWithConnections = companies.filter((c) => c.erpConnectionName);

  return (
    <div className="mb-4 border border-gray-200 rounded-lg bg-white">
      {/* Report title + primary params */}
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">{reportName}</h2>
        <div className="mt-1 flex gap-6 text-sm text-gray-600">
          <span>
            <span className="font-medium text-gray-700">Company:</span> {selectedCompany}
          </span>
          <span>
            <span className="font-medium text-gray-700">Period:</span> {selectedPeriod}
          </span>
        </div>
      </div>

      {/* Collapsible sections */}
      <div className="px-4 py-2 space-y-1 text-sm">
        {dateFiscalInfo && (
          <details className="group">
            <summary className="cursor-pointer text-gray-500 hover:text-gray-700 select-none py-1">
              Date &amp; Fiscal Information
            </summary>
            <div className="pl-4 pb-2 grid grid-cols-2 gap-x-8 gap-y-0.5 text-gray-600">
              <span>Current Year: <span className="text-gray-800">{dateFiscalInfo.currentYear}</span></span>
              <span>Period Start: <span className="text-gray-800">{dateFiscalInfo.currentPeriodStartDate}</span></span>
              <span>Period End: <span className="text-gray-800">{dateFiscalInfo.currentPeriodEndDate}</span></span>
              <span>Fiscal Year Start: <span className="text-gray-800">{dateFiscalInfo.fiscalYearStart}</span></span>
              <span>Fiscal Year End: <span className="text-gray-800">{dateFiscalInfo.fiscalYearEnd}</span></span>
            </div>
          </details>
        )}

        {erpConnections && erpConnections.length > 0 && (
          <details className="group">
            <summary className="cursor-pointer text-gray-500 hover:text-gray-700 select-none py-1">
              ERP Connections
            </summary>
            <div className="pl-4 pb-2 space-y-0.5 text-gray-600">
              {erpConnections.map((conn) => (
                <div key={conn.id}>
                  {conn.name} <span className="text-gray-400">({conn.erpType.toUpperCase()})</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {companiesWithConnections.length > 0 && (
          <details className="group">
            <summary className="cursor-pointer text-gray-500 hover:text-gray-700 select-none py-1">
              Company Data Sources
            </summary>
            <div className="pl-4 pb-2">
              <table className="text-gray-600">
                <thead>
                  <tr>
                    <th className="text-left font-medium text-gray-700 pr-6 pb-0.5">Company</th>
                    <th className="text-left font-medium text-gray-700 pb-0.5">ERP Connection</th>
                  </tr>
                </thead>
                <tbody>
                  {companiesWithConnections.map((c) => (
                    <tr key={c.id}>
                      <td className="pr-6">{c.name}</td>
                      <td>{c.erpConnectionName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
